/**
 * Read what the user sees from the DOM (plan 1968 F13) — display strings exactly as
 * rendered, so formatting is never re-implemented here.
 *
 * - captureScreenText: text fallback = innerText of the page content region
 *   (`[data-ai-screen-root]` in MainLayout), minus nav/sidebars, editable areas and
 *   anything marked `data-ai-screen-exclude`. The AI panel, rails, header and toasts
 *   live outside the root. Hidden elements (display:none tabs) are skipped.
 * - readDomTable: header labels + first rows of the first <table>, cells joined " | ".
 */

import { PAGE_CONTEXT_LIMITS, type ScreenTable } from "./types";

export const SCREEN_ROOT_ATTR = "data-ai-screen-root";
export const SCREEN_EXCLUDE_ATTR = "data-ai-screen-exclude";

/** Never read: navigation, sidebars, editable content (may hold unsaved edits), form controls, media. */
const EXCLUDE_SELECTOR = [
  "nav",
  "aside",
  '[role="navigation"]',
  '[role="banner"]',
  '[role="complementary"]',
  '[role="dialog"]',
  '[aria-hidden="true"]',
  "[hidden]",
  "[contenteditable]:not([contenteditable='false'])",
  "input",
  "textarea",
  "select",
  "button[role='combobox']",
  "script",
  "style",
  "noscript",
  "template",
  "iframe",
  "svg",
  "canvas",
  `[${SCREEN_EXCLUDE_ATTR}]`,
].join(",");

const ROW_SEPARATOR = " | ";

/** One-line display string. */
export function oneLine(text: string, max: number = PAGE_CONTEXT_LIMITS.maxString): string {
  const line = text.replace(/\s+/g, " ").trim();
  return line.length > max ? line.slice(0, max) : line;
}

function isRendered(el: Element): boolean {
  const check = (el as Element & { checkVisibility?: () => boolean }).checkVisibility;
  return typeof check === "function" ? check.call(el) : true;
}

/** innerText where supported (layout-aware), textContent otherwise (jsdom). */
function visibleText(el: Element): string {
  const inner = (el as HTMLElement).innerText;
  return typeof inner === "string" ? inner : (el.textContent ?? "");
}

function textOf(el: Element): string {
  if (el.matches(EXCLUDE_SELECTOR) || !isRendered(el)) return "";
  if (!el.querySelector(EXCLUDE_SELECTOR)) return visibleText(el);
  const parts: string[] = [];
  el.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) parts.push(node.textContent ?? "");
    else if (node.nodeType === Node.ELEMENT_NODE) parts.push(textOf(node as Element));
  });
  return parts.join("\n");
}

export function collapseText(text: string): string {
  return text
    .replace(/[ \t\f\v\u00a0]+/g, " ")
    .replace(/ *\n[\s]*/g, "\n")
    .trim();
}

/** Page content text, whitespace collapsed, capped at maxScreenText. Empty when no root. */
export function captureScreenText(doc: ParentNode | null = typeof document === "undefined" ? null : document): {
  text: string;
  truncated: boolean;
} {
  const root = doc?.querySelector(`[${SCREEN_ROOT_ATTR}]`);
  if (!root) return { text: "", truncated: false };
  // The root itself is `display: contents` (no box) — read its children
  const parts: string[] = [];
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) parts.push(textOf(node as Element));
  });
  const text = collapseText(parts.join("\n"));
  const max = PAGE_CONTEXT_LIMITS.maxScreenText;
  return text.length > max ? { text: text.slice(0, max), truncated: true } : { text, truncated: false };
}

/** Clickable controls; a menu trigger (aria-haspopup) is a sortable column label, not an action. */
const ACTION_CONTROL = "button:not([aria-haspopup]), [role='button']:not([aria-haspopup]), [role='menuitem']";

/** Header label, or "" when the cell is empty or holds only an action control (e.g. a column picker). */
function headerLabel(th: Element): string {
  const label = oneLine(visibleText(th));
  if (!label) return "";
  const controls = Array.from(th.querySelectorAll(ACTION_CONTROL));
  const controlText = oneLine(controls.map((c) => visibleText(c)).join(" "));
  return controls.length && controlText === label ? "" : label;
}

/** Drop trailing empty cells, then join. */
function joinCells(cells: string[]): string {
  let end = cells.length;
  while (end > 0 && !cells[end - 1]) end -= 1;
  return oneLine(cells.slice(0, end).join(ROW_SEPARATOR));
}

/**
 * The first rendered <table> under `container`, as display strings. Columns kept: those
 * with a text label (select boxes, thumbnails, action-only headers like a column picker
 * are dropped) that are not empty in every captured row. Rows whose cell count differs
 * from the header (empty state, expanded sub-rows) are skipped; trailing empty cells trimmed.
 */
export function readDomTable(
  container: ParentNode | null | undefined,
  options: { title?: string; total?: number; maxRows?: number } = {},
): ScreenTable | null {
  const table = container?.querySelector("table");
  if (!table || !isRendered(table)) return null;
  const headerRow = table.querySelector("thead tr:last-child");
  if (!headerRow) return null;
  const headerCells = Array.from(headerRow.children);
  const labelled = headerCells.map((th, index) => ({ label: headerLabel(th), index })).filter((h) => h.label);
  if (!labelled.length) return null;

  const maxRows = options.maxRows ?? PAGE_CONTEXT_LIMITS.maxScreenRows;
  const matrix: string[][] = [];
  for (const tr of Array.from(table.querySelectorAll("tbody tr"))) {
    if (matrix.length >= maxRows) break;
    const cells = Array.from(tr.children);
    if (cells.length !== headerCells.length || !isRendered(tr)) continue;
    const values = labelled.map(({ index }) => oneLine(textOf(cells[index])));
    if (values.some(Boolean)) matrix.push(values);
  }

  const keep = labelled
    .map((h, col) => ({ ...h, col }))
    .filter(({ col }) => !matrix.length || matrix.some((values) => values[col]))
    .slice(0, PAGE_CONTEXT_LIMITS.maxScreenColumns);
  const rows = matrix.map((values) => joinCells(keep.map(({ col }) => values[col])));
  return {
    ...(options.title ? { title: oneLine(options.title) } : {}),
    columns: keep.map((h) => h.label),
    rows,
    shown: rows.length,
    ...(typeof options.total === "number" ? { total: options.total } : {}),
  };
}
